import React, { useState } from 'react';
import {
  Card,
  CardBody,
  Avatar,
  Button,
  Textarea
} from '@heroui/react';
import {
  Send,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const CommentCreator = ({ 
  post, 
  parentCommentId = null, 
  onCommentAdded, 
  onCancel,
  placeholder = "Escreva um comentário...",
  autoFocus = false 
}) => {
  const { user } = useAuth();
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!commentText.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const postId = post._id?.['$oid'] || post._id;
      const formData = new FormData();
      formData.append('comment[text]', commentText.trim());
      
      if (parentCommentId) {
        formData.append('comment_id', parentCommentId);
      }

      const response = await fetch(`/api/v1/posts/${postId}/comments`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const newComment = data.comment; // Updated field name
        const updatedPost = data.post; // New field with updated post data
        
        // Add user info to the comment (since API might not include it)
        if (!newComment.user) {
          newComment.user = user;
        }
        
        // Clear form
        setCommentText('');
        
        // Notify parent component with both comment and updated post
        if (onCommentAdded) {
          onCommentAdded(newComment, updatedPost);
        }
      } else {
        console.error('Failed to create comment');
        alert('Erro ao criar comentário');
      }
    } catch (error) {
      console.error('Error creating comment:', error);
      alert('Erro ao criar comentário');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setCommentText('');
    if (onCancel) {
      onCancel();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit(e);
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!user) {
    return (
      <Card className="bg-gray-50">
        <CardBody className="p-4 text-center">
          <p className="text-gray-600">Faça login para comentar</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm rounded-nextui bg-white border border-gray-300">
      <CardBody className="p-4">
        <form onSubmit={handleSubmit}>
          <div className="flex space-x-3">
            <Avatar
              src={user.avatar}
              name={user.username}
              size="sm"
              className="flex-shrink-0 mt-1"
            />
            
            <div className="flex-1 space-y-4">
              <Textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                minRows={3}
                maxRows={10}
                size="sm"
                variant="bordered"
                autoFocus={autoFocus}
                classNames={{
                  input: "resize-none px-3 py-3",
                  inputWrapper: "border-gray-200 hover:border-gray-300 focus-within:border-primary rounded-nextui-sm bg-white min-h-[80px]",
                  base: "rounded-nextui-sm min-h-[80px]"
                }}
              />
              
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Ctrl+Enter para enviar • Esc para cancelar
                </div>
                
                <div className="flex space-x-2">
                  {onCancel && (
                    <Button
                      size="sm"
                      variant="light"
                      startContent={<X className="w-4 h-4" />}
                      onPress={handleCancel}
                      isDisabled={isSubmitting}
                      className="rounded-nextui-sm"
                    >
                      Cancelar
                    </Button>
                  )}
                  
                  <Button
                    size="sm"
                    color="primary"
                    type="submit"
                    startContent={<Send className="w-4 h-4" />}
                    isDisabled={!commentText.trim() || isSubmitting}
                    isLoading={isSubmitting}
                    className="rounded-nextui-sm"
                  >
                    {isSubmitting ? 'Enviando...' : 'Comentar'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default CommentCreator;
